import {computed, inject, Injectable, signal} from '@angular/core';
import {BaseApiService} from "./base-api.service";
import {environment} from "../../../environments/environment";
import {BehaviorSubject, map, Observable, tap} from "rxjs";
import {UserCredentials} from "../models/auth/user-credentials.model";
import {CURRENT_USER, SIGN_OUT, TOKEN_EXPIRY_BUFFER_MS, USER_PERMISSIONS, USER_TOKEN} from "../../util/constants";
import {Response} from "../models/response.model";
import {UserToken} from "../models/auth/user-token.model";
import {User} from "../models/auth/user.model";
import {jwtDecode, JwtPayload} from "jwt-decode";
import {longTimeout} from "../../util/utils";
import {Router} from "@angular/router";
import {PermissionMap} from "../models/types/permission/permission-map.model";
import {PermissionAction} from "../models/types/permission/permission-action.model";
import {DashboardPreviewService} from '../services/dashboard-preview.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService extends BaseApiService {
  router = inject(Router)
  private preview = inject(DashboardPreviewService);

  private clearExpiryTimer!: () => void;
  private userToken = new BehaviorSubject<string | null>(null);
  private currentUser = new BehaviorSubject<User | null>(null);
  private _permissions = signal<PermissionMap>({});
  private _permissionsReady = signal(false)

  public isLoggedIn$ = this.userToken.asObservable().pipe(map(token => !!token && !!localStorage.getItem(USER_TOKEN)));
  public currentUser$ = this.currentUser.asObservable();
  public readonly permissions = computed(() => this.preview.active()
    ? this.preview.permissions() ?? {} : this._permissions());
  public readonly permissionsReady = computed(() => this._permissionsReady());

  constructor() {
    super();
    const storedUser = localStorage.getItem(CURRENT_USER);
    const storedToken = localStorage.getItem(USER_TOKEN);
    const storedPermissions = localStorage.getItem(USER_PERMISSIONS)

    if (storedToken) {
      const token = JSON.parse(storedToken);
      this.userToken.next(token);
      this.scheduleTokenExpiry(token);
    }
    if (storedUser) {
      this.currentUser.next(JSON.parse(storedUser))
    }
    if (storedPermissions) {
      const permissions = JSON.parse(storedPermissions);
      this._permissions.set(permissions);
      this._permissionsReady.set(true);
    }

    this.fetchPermissions()
  }

  public login(credentials: UserCredentials): Observable<Response<UserToken>> {
    return this.http.post<Response<UserToken>>(`${environment.baseApiExternal}sign-in`, credentials)
      .pipe(
        tap((response: Response<UserToken>) => {
          this.storeInStorage(USER_TOKEN, JSON.stringify(response.data.token))
          this.userToken.next(response.data.token);
          this.fetchPermissions();
          this.fetchCurrentUser();
          this.scheduleTokenExpiry(response.data.token);
        })
      );
  }

  public logout(skipBackendCall: boolean = false): void {
    const handleLogout = () => {
      this.preview.clear();
      this.preview.allowed.set(false);
      this.preview.users.set([]);
      this.clearAuthData();
      this.clearExpiryTimer?.();
      this.router.navigate(['/login'])
        .then(() => this.clearPermissions())
    }

    if (skipBackendCall) {
      handleLogout();
    } else {
      this.http
        .post<Response<{ message: string }>>(`${environment.baseApiExternal}${SIGN_OUT}`, {})
        .subscribe(() => handleLogout());
    }
  }

  public getAuthToken(): string | null {
    return this.userToken.value;
  }

  public can(entityName: string, action: PermissionAction): boolean {
    if (this.preview.active() && !['list', 'show'].includes(action)) return false;
    return this.permissions()?.[entityName]?.includes(action) ?? false;
  }

  public getActions(entityName: string) {
    return this.permissions()[entityName];
  }

  private fetchCurrentUser(): void {
    this.http
      .get<Response<{ user: User }>>(`${environment.baseApiExternal}user/current`)
      .subscribe(response => {
        this.currentUser.next(response.data.user);
        this.storeInStorage(CURRENT_USER, JSON.stringify(response.data.user))
      })
  }

  private fetchPermissions(): void {
    this.http
      .get<Response<{ permissions: PermissionMap }>>(this.baseUrl + `entity/permissions`,
        {headers: {'Skip-Dashboard-Preview': '1'}})
      .subscribe(response => {
        this._permissionsReady.set(true);
        this._permissions.set(response.data.permissions);
        this.storeInStorage(USER_PERMISSIONS, JSON.stringify(response.data.permissions));
      })
  }

  private storeInStorage(key: string, value: string): void {
    localStorage.setItem(key, value)
  }

  private scheduleTokenExpiry(token: string): void {
    const decoded = jwtDecode<JwtPayload>(token);
    const expiresAt = (decoded.exp ?? 0) * 1000;
    const now = Date.now();
    const timeout = Math.floor(expiresAt - now - TOKEN_EXPIRY_BUFFER_MS);

    if (timeout > 0) {
      this.clearExpiryTimer = longTimeout(() => {
        alert('Session expired. You have been logged out.');
        this.logout();
      }, timeout)
    } else {
      this.logout();
    }
  }

  private clearAuthData() {
    this.userToken.next(null);
    this.currentUser.next(null);
    localStorage.removeItem(USER_TOKEN);
    localStorage.removeItem(CURRENT_USER);
    localStorage.removeItem(USER_PERMISSIONS);
  }

  private clearPermissions() {
    this._permissions.set({});
    this._permissionsReady.set(false);
  }
}
